
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Plus, Settings } from "lucide-react";
import { Lead, LeadStatus } from "@/types";
import { MOCK_DEALER_ID, deleteLeadById, deleteMultipleLeads, fetchLeadsByDealer } from "@/services/leads";
import { toast } from "@/hooks/use-toast";
import DashboardAnalytics from "@/components/DashboardAnalytics";
import CampaignActions from "@/components/CampaignActions";
import QueuedLeadsCampaign from "@/components/QueuedLeadsCampaign";
import LeadTable from "@/components/dashboard/LeadTable";
import LeadFilters from "@/components/dashboard/LeadFilters";
import DeleteLeadsDialog from "@/components/dashboard/DeleteLeadsDialog";
import { useIsMobile } from "@/hooks/use-mobile";

type StatusTab = LeadStatus | "all";

const Dashboard = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<StatusTab>("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLeadUploadModal, setShowLeadUploadModal] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const leadsData = await fetchLeadsByDealer(MOCK_DEALER_ID);
      setLeads(leadsData);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast({
        title: "Failed to load leads",
        description: "Could not fetch lead data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleAddLeadsClick = () => {
    if (isMobile) {
      navigate("/leads/upload");
    } else {
      setShowLeadUploadModal(true);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (selectedLeads.size === 0) return;
    
    try {
      const leadIds = Array.from(selectedLeads);
      const { success, error, count } = await deleteMultipleLeads(leadIds);
      
      if (success) {
        toast({
          title: "Leads deleted",
          description: `Successfully deleted ${count} lead${count !== 1 ? 's' : ''}.`,
        });
        fetchLeads();
        setSelectedLeads(new Set());
        setSelectAll(false);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      console.error("Error deleting leads:", error);
      toast({
        title: "Error deleting leads",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      const { success, error } = await deleteLeadById(id);
      
      if (success) {
        toast({
          title: "Lead deleted",
          description: "The lead was successfully deleted.",
        });
        fetchLeads();
      } else {
        throw new Error(error);
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast({
        title: "Error deleting lead",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    }
  };

  const handleSelectLead = (id: string, isSelected: boolean) => {
    const newSelectedLeads = new Set(selectedLeads);
    isSelected ? newSelectedLeads.add(id) : newSelectedLeads.delete(id);
    setSelectedLeads(newSelectedLeads);
  };

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const newSelectedLeads = new Set<string>();
      const filteredLeads = leads.filter(lead => 
        activeTab === "all" || lead.status === activeTab
      );
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredLeads.filter(lead => 
          lead.name.toLowerCase().includes(query) || 
          lead.email.toLowerCase().includes(query) || 
          lead.phone.toLowerCase().includes(query) ||
          (lead.meta_notes && lead.meta_notes.toLowerCase().includes(query))
        ).forEach(lead => lead.id && newSelectedLeads.add(lead.id));
      } else {
        filteredLeads.forEach(lead => lead.id && newSelectedLeads.add(lead.id));
      }
      
      setSelectedLeads(newSelectedLeads);
    } else {
      setSelectedLeads(new Set());
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as StatusTab);
    // Reset selection when tab changes
    setSelectedLeads(new Set());
    setSelectAll(false);
  };

  const leadCounts = useMemo(() => {
    const counts = { 
      all: leads.length,
      queued: 0,
      "in-progress": 0,
      completed: 0,
      reactivated: 0,
      "not-interested": 0
    };
    
    leads.forEach(lead => {
      if (counts.hasOwnProperty(lead.status)) {
        counts[lead.status as LeadStatus]++;
      }
    });
    
    return counts;
  }, [leads]);

  return (
    <div className="container max-w-7xl mx-auto py-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold">Leads Dashboard</h1>
        <div className="flex gap-2">
          <Button onClick={handleAddLeadsClick} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            <span>Add Leads</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden md:inline">Admin Settings</span>
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <DashboardAnalytics leads={leads} />
      </div>

      <QueuedLeadsCampaign 
        leads={leads} 
        refreshLeads={fetchLeads} 
        showLeadUploadModal={showLeadUploadModal}
        setShowLeadUploadModal={setShowLeadUploadModal}
      />

      <div className="bg-white shadow-sm rounded-lg border overflow-hidden leads-table-section">
        <div className="p-4 space-y-6">
          <LeadFilters 
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedLeads={selectedLeads}
            setShowDeleteDialog={setShowDeleteDialog}
            handleSelectAllChange={handleSelectAllChange}
            leadCounts={leadCounts}
          />

          {selectedLeads.size > 0 && (
            <div className="flex items-center gap-2">
              <CampaignActions 
                selectedLeads={selectedLeads} 
                fetchLeads={fetchLeads} 
                allLeads={leads}
              />
            </div>
          )}

          <Tabs value={activeTab}>
            <TabsContent value={activeTab} className="m-0">
              <LeadTable 
                leads={leads}
                isLoading={isLoading}
                activeTab={activeTab}
                searchQuery={searchQuery}
                selectedLeads={selectedLeads}
                handleSelectLead={handleSelectLead}
                handleDeleteLead={handleDeleteLead}
                selectAll={selectAll}
                handleSelectAllChange={handleSelectAllChange}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <DeleteLeadsDialog 
        showDialog={showDeleteDialog}
        setShowDialog={setShowDeleteDialog}
        selectedLeadsCount={selectedLeads.size}
        onConfirmDelete={handleDeleteConfirmed}
      />
    </div>
  );
};

export default Dashboard;
